import { jwtDecode } from 'jwt-decode';
import { ROLE_CLAIM } from '../config/config';
import { clearNotificationBell } from './notifications';

export const SetToken = (token) => localStorage.setItem('isToken', token);
export const SetRefreshToken = (refreshToken) => {
    localStorage.setItem('isRefreshToken', refreshToken)
};
export const SetUser = (user) => localStorage.setItem("isUser", JSON.stringify(user));

/**
 * Reads a stored token, treating the strings "undefined" and "null" as no token at all.
 *
 * #111: localStorage stringifies whatever it is given. Until loginUserAPI stopped writing on a
 * failed login, every wrong password left "undefined" behind, and SetToken(null) leaves "null" -
 * both truthy, so the header showed Dashboard and every request went out as "Bearer undefined".
 * Browsers that already hold such a value keep it until the next sign-in or sign-out, so the
 * read side has to ignore it too.
 *
 * @param {string} key The localStorage key.
 * @returns {string|null} The token, or null when none is stored.
 */
const readStoredToken = (key) => {
    const value = localStorage.getItem(key);
    return value === 'undefined' || value === 'null' ? null : value;
};

export const GetToken = () => readStoredToken('isToken');
export const isExpired = () => localStorage.getItem('token');
export const GetRefreshToken = () => readStoredToken('isRefreshToken');
export const GetUser = () => localStorage.getItem('isUser');
export const RemoveToken = () => localStorage.removeItem('isToken');
export const setRoles = (role) => localStorage.setItem('role', role);
export const RemovRoles = () => localStorage.removeItem('role');
export const RemoveRefreshToken = () => localStorage.removeItem('isRefreshToken');

/**
 * Where Login.js keeps the email "Remember me" fills in next time. Survives sign-out on purpose -
 * that is what remembering is for - and holds nothing but the address.
 */
export const REMEMBERED_EMAIL_KEY = 'rememberedEmail';

/**
 * Deletes the Remember-me blob older builds left behind (FE-01).
 *
 * "userData" held the user's password, AES-encrypted with REACT_APP_API_SECRET - a key CRA compiles
 * into the public bundle, so the blob was as good as plaintext. Nothing writes it any more, but a
 * browser that ticked Remember me before keeps it until something removes it, and only one of the
 * sign-out paths ever did. index.js calls this on every load, so it goes whichever page the user
 * opens next, not only /Login.
 */
export const purgeLegacyRememberMe = () => localStorage.removeItem('userData');

/**
 * Reads the role and expiry out of the stored access token.
 *
 * THIS DECODES THE TOKEN, IT DOES NOT VERIFY IT. The signature is an HMAC over a secret only the
 * server holds, so nothing in the browser can tell a real token from an edited one. What reading
 * the role here buys over the `role` localStorage key is that the screens cannot disagree with
 * the server: a token edited to claim another role fails the server's signature check and every
 * request it makes is refused, whereas an edited `role` string sits next to a perfectly valid
 * token. The server's [CustomAuthorize] is the authorization boundary; this only keeps the UI
 * in line with it.
 *
 * @returns {{role: (string|undefined), exp: (number|undefined)}|null} null when there is no
 *   token or it is not a decodable JWT.
 */
export const readSession = () => {
    const token = GetToken();
    if (!token) return null;
    try {
        const claims = jwtDecode(token);
        return { role: claims?.[ROLE_CLAIM], exp: claims?.exp };
    } catch {
        return null;
    }
};

/**
 * Whether the access token read by readSession() is past its `exp`.
 *
 * An expired access token is not an ended session - the refresh token outlives it, and
 * refreshSession() in apiClient.js renews it - so a true here means "renew", never "sign out".
 * A token with no `exp` is treated as live and left to the server, rather than refreshed in a
 * loop that could never produce one.
 *
 * @param {{exp: (number|undefined)}|null} session From readSession().
 * @returns {boolean}
 */
export const isSessionExpired = (session) =>
    typeof session?.exp === 'number' && session.exp * 1000 <= Date.now();

/**
 * When a token expires, read from its `exp` claim - for the access token and the refresh token
 * alike, since the server issues both as JWTs.
 *
 * Decoded, not verified, like readSession(): it only decides WHEN to renew. The server still
 * decides whether a token is any good.
 *
 * @param {string|null|undefined} token A stored token.
 * @returns {number|null} The expiry in epoch milliseconds, or null when there is no token, it is
 *   not a decodable JWT, or it carries no `exp`.
 */
export const readTokenExpiry = (token) => {
    if (!token) return null;
    try {
        const { exp } = jwtDecode(token);
        return typeof exp === 'number' ? exp * 1000 : null;
    } catch {
        return null;
    }
};

/**
 * Each role's dashboard, keyed by the role name as the server spells it. Manager and Admin share
 * /SuperAdmin - Login.js, Sidebar.js and SuperDashboard.js all treat the two alike.
 */
const ROLE_HOME = new Map([
    ['Web Admin', '/WebAdmin/Dashboard'],
    ['Manager', '/SuperAdmin/SuperDashboard'],
    ['Admin', '/SuperAdmin/SuperDashboard'],
    ['Player', '/Player/Player_dashboard'],
]);

/**
 * The dashboard a role lands on.
 *
 * @param {string|null|undefined} role A role name as the server spells it.
 * @returns {string|null} The path, or null for a missing or unrecognised role.
 */
export const homeForRole = (role) => ROLE_HOME.get(role) ?? null;

/**
 * Whether two email addresses name the same account: compared trimmed and case-insensitively, and
 * never true for a missing or blank one.
 *
 * The API's sign-in notice (ReceiveUserStatus) carries the address exactly as it was typed on the
 * login form, while "isUser" holds the account's stored one - so "Jane@x.com" signing in did not
 * match "jane@x.com" and the old session was never told.
 *
 * @param {*} a
 * @param {*} b
 * @returns {boolean}
 */
export const sameEmail = (a, b) => {
    if (typeof a !== 'string' || typeof b !== 'string') return false;
    const left = a.trim().toLowerCase();
    return left !== '' && left === b.trim().toLowerCase();
};

/**
 * Records that the player has finished or closed the User Instructions tour.
 *
 * The login response carries isUserInstruction = true only on a player's first login (the
 * procedure clears it server side), but Login.js copies it into "isUser" for the whole session.
 * Until that copy is cleared, every reload of the Dashboard or Auctions page replays the tour.
 * Also drops the "instruction" request the sidebar's User Instructions item sets, so a tour
 * opened from there and closed early is not replayed either.
 */
export const endUserInstructions = () => {
    const user = JSON.parse(GetUser());
    if (user?.isUserInstruction) SetUser({ ...user, isUserInstruction: false });
    localStorage.removeItem('instruction');
};

/**
 * Fired on window when setStoredEmailVerified changes the stored flag, so the banner and the email
 * switches already on screen follow at once. Other tabs learn it from the "storage" event.
 */
export const EMAIL_VERIFICATION_EVENT = 'xcutta:emailVerification';

const readStoredUser = () => {
    try {
        return JSON.parse(GetUser());
    } catch {
        return null;
    }
};

/**
 * Whether the signed-in account's email is verified, as stored in "isUser" from the sign-in
 * response and kept current by the banner and the email settings pages.
 *
 * @returns {boolean|null} null when it is not known: no session, or one signed in before the field
 *   existed.
 */
export const readStoredEmailVerified = () => {
    const verified = readStoredUser()?.emailVerified;
    return typeof verified === 'boolean' ? verified : null;
};

/**
 * Records whether the signed-in account's email is verified in "isUser", and tells the screens on
 * show (EMAIL_VERIFICATION_EVENT). Does nothing without a stored user, or when nothing changes.
 *
 * @param {boolean} verified
 */
export const setStoredEmailVerified = (verified) => {
    const user = readStoredUser();
    if (!user || user.emailVerified === verified) return;
    SetUser({ ...user, emailVerified: verified });
    window.dispatchEvent(new Event(EMAIL_VERIFICATION_EVENT));
};

/**
 * Picks the subscription to display from the list returned by GetSubScription().
 *
 * A live Stripe subscription always wins over a legacy entitlement. Legacy rows can carry a
 * far-future end date, so taking the last element alone would hide a real subscription behind
 * an older grant and show the user "Auto Renew: No" with no way to cancel.
 *
 * @param {Array} list Subscription rows as returned by the API.
 * @returns {object|null} The subscription to display, or null when there is none.
 */
export const pickCurrentSubscription = (list = []) => {
    if (!Array.isArray(list) || list.length === 0) return null;
    const stripeSubscription = list.filter((item) => item?.isStripeSubscription).pop();
    return stripeSubscription ?? list[list.length - 1] ?? null;
};

/**
 * Whether a subscription currently entitles the user to bid.
 *
 * Reads the server-computed flag rather than comparing dates in the browser: access can be
 * revoked before the end date (a failed renewal payment), which a date comparison cannot see.
 *
 * @param {object} subscription A row from pickCurrentSubscription().
 * @returns {boolean}
 */
export const isSubscriptionActive = (subscription) => subscription?.isActive === true;

/** Where forceLogout leaves its reason, for the sign-in screen to pick up and show. */
export const SESSION_ENDED_KEY = 'sessionEndedMessage';

/**
 * Reads what forceLogout stashed under SESSION_ENDED_KEY.
 *
 * #199: a plain string is a reason the session ENDED, shown as an error, exactly as before - and it
 * stays stored as the raw string, which is what everything that already stashes one writes. An
 * account deletion is not a failure, so it stashes `{ type: 'info', text }` as JSON instead.
 *
 * @param {string|null} stashed The stored value.
 * @returns {{type: 'info'|'error', text: string}|null} What to show, or null when there is nothing.
 */
export const readSessionNotice = (stashed) => {
    if (!stashed) return null;

    if (stashed.startsWith('{')) {
        try {
            const notice = JSON.parse(stashed);
            if (typeof notice?.text === 'string' && notice.text.trim()) {
                return { type: notice.type === 'info' ? 'info' : 'error', text: notice.text };
            }
        } catch {
            // Not ours after all: shown as the text it is.
        }
    }

    return { type: 'error', text: stashed };
};

/** Where a signed-out visitor's destination waits while they sign in (#199). */
export const RETURN_PATH_KEY = 'returnAfterSignIn';

/**
 * The private pages a signed-out visitor is brought back to after signing in (#199).
 *
 * An allow-list, not "wherever they were": most screens open with router state a fresh sign-in
 * cannot supply (SecodaryPlaceBid, the bid pages), and would break if reached by URL alone. Account
 * Settings is here because it is the account-deletion link given to Google Play, which must work
 * for someone who is not signed in. Lower case; compared case-insensitively.
 */
const RETURNABLE_PATHS = ['/player/account_settings'];

/**
 * Remembers where a signed-out visitor was going, when it is a page they may be returned to.
 *
 * @param {string} path The pathname they asked for.
 */
export const rememberReturnPath = (path) => {
    if (typeof path === 'string' && RETURNABLE_PATHS.includes(path.toLowerCase())) {
        sessionStorage.setItem(RETURN_PATH_KEY, path);
    }
};

/**
 * Takes the remembered destination, if any, and forgets it.
 *
 * @returns {string|null} A path from the allow-list, or null.
 */
export const takeReturnPath = () => {
    const path = sessionStorage.getItem(RETURN_PATH_KEY);
    sessionStorage.removeItem(RETURN_PATH_KEY);
    return typeof path === 'string' && RETURNABLE_PATHS.includes(path.toLowerCase()) ? path : null;
};

/**
 * The three role-scoped subtrees in App.js. Everything else - the marketing pages, /Login,
 * /PaymentSuccess - is public and must never be redirected away from: PaymentSuccess in
 * particular is reached from Stripe's domain minutes later, and bouncing it would destroy the
 * session_id it is still reconciling.
 */
const PRIVATE_PATH_PREFIXES = ['/webadmin', '/superadmin', '/player'];

/**
 * Ends the session and returns the user to the sign-in screen.
 *
 * Clears `role` as well as the tokens. PrivateRoute no longer reads it - it takes the role from
 * the token - but the sidebar, the top bar and a dozen screens still do, and a role left behind
 * describes a session that no longer exists. Uses the Remove* helpers rather
 * than SetToken(null), which writes the literal string "null" and reads back as truthy.
 *
 * The message is stashed rather than toasted here: a toast fired immediately before a hard
 * navigation never gets painted, which is why the old APIConfig.js message was invisible. The
 * sign-in screen shows it on arrival instead.
 *
 * WEB-P1-161: THE MESSAGE IS STASHED BEFORE THE PUBLIC-PATH RETURN, not after it. A session can
 * end while the user is on a public page - Home fires an authenticated call - and the early
 * return below is only about not YANKING them off that page. It used to skip the stash as well,
 * so the reason was discarded rather than deferred and the eventual trip to /Login explained
 * nothing. Clearing still happens either way: the session really is over.
 *
 * #196: the notification bell is cleared too, or the next account to sign in on this browser
 * would be shown this one's auction reminders.
 *
 * #199: A REASON ALREADY WAITING IS KEPT. Once the server has ended a session, any request still in
 * flight is refused and its refresh calls this again with the generic SESSION_ENDED_MESSAGE; an
 * account deletion's notice must not be replaced by it. The sign-in screen removes the stash when it
 * shows it, so a later session's reason is never blocked by an old one that was seen.
 *
 * #199: on the way to /Login from a page in the return allow-list, the page is remembered, so that
 * signing in again goes back there (rememberReturnPath). Pass `{ returnHere: false }` when the page
 * has nothing left to offer, as after an account deletion.
 *
 * @param {string|{type: 'info'|'error', text: string}} [message] Why the session ended, shown on the
 *   sign-in screen. A string is shown as an error; an object as its type says.
 * @param {{returnHere?: boolean}} [options]
 */
export const forceLogout = (message, { returnHere = true } = {}) => {
    RemoveToken();
    RemoveRefreshToken();
    RemovRoles();
    localStorage.removeItem('isUser');
    localStorage.removeItem('instruction');
    clearNotificationBell();

    if (message && !sessionStorage.getItem(SESSION_ENDED_KEY)) {
        sessionStorage.setItem(SESSION_ENDED_KEY, typeof message === 'string' ? message : JSON.stringify(message));
    }

    const path = (window.location.pathname || '').toLowerCase();
    if (!PRIVATE_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))) return;

    if (returnHere) rememberReturnPath(window.location.pathname);

    window.location.assign('/Login');
};
/**
 * Whether the user may buy a plan right now, so the plan selector can be hidden instead of
 * letting them click into a rejection.
 *
 * The test is deliberately `!== false`, never `!canPurchase`. The flag is absent for a user with
 * no subscription rows at all and for a logged-out visitor, and both of those may purchase -- so
 * treating a missing flag as "blocked" would hide the selector from every new user.
 *
 * This answers the user-level question only. A plan can still be rejected for its own reasons
 * (missing, not purchasable, no Stripe price id), which is why the checkout call keeps handling a
 * `status === false` response even when this returns true.
 *
 * @param {object} subscription A row from pickCurrentSubscription().
 * @returns {boolean}
 */
export const canPurchaseSubscription = (subscription) => subscription?.canPurchase !== false;

/** Shown when a request fails and the server gave us nothing readable to show instead. */
export const GENERIC_API_ERROR = 'Something went wrong. Please try again.';

/**
 * Pulls a displayable sentence out of an API result, whatever shape it arrived in.
 *
 * Three shapes reach the components, and until now each was read differently - or not at all:
 *   - a business refusal, HTTP 200 with `{ status: false, message }` from the stored procedure;
 *   - a validation failure, HTTP 400 with ASP.NET Core's ValidationProblemDetails, which carries
 *     `errors` and `title` and has NO `message` member at all;
 *   - a 500 from ExceptionMiddleware, `{ status: false, message, traceId }`.
 *
 * The 400 shape is why this exists. `[Required]`/`[Range]` on an input model produce it, and
 * `toast.error(res?.message)` against it renders an empty toast - the user sees a blank box and
 * no reason. Reading `errors` first gives them the actual field message.
 *
 * Accepts either a full axios response or a bare body, because the service wrappers are not
 * consistent about which they return: TeamReg hands back the whole response, TeamUpdate and
 * TeamDelete hand back `response.data`.
 *
 * @param {object} result An axios response, a response body, or undefined.
 * @param {string} [fallback] Shown when nothing readable can be found.
 * @returns {string} A sentence safe to pass to toast.error.
 */
export const readApiMessage = (result, fallback = GENERIC_API_ERROR) => {
    // A full axios response carries the body under .data; a bare body does not. Testing for
    // .data alone is not enough - ValidationProblemDetails has no .data, but neither does an
    // already-unwrapped body, so prefer .data only when it looks like an object.
    const body = result?.data && typeof result.data === 'object' ? result.data : result;

    if (typeof body?.message === 'string' && body.message.trim()) return body.message;

    // ValidationProblemDetails.errors is { fieldName: [msg, ...] }. Take the first message we
    // find rather than joining them all: these are field-level and the toast is one line.
    const firstFieldError = Object.values(body?.errors ?? {})
        .flat()
        .find((entry) => typeof entry === 'string' && entry.trim());
    if (firstFieldError) return firstFieldError;

    if (typeof body?.title === 'string' && body.title.trim()) return body.title;

    return fallback;
};
